import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError, TextField } from '@/components/primitives'
import { LoadingRegion, Skeleton } from '@/components/states'
import { ApiFailure, api } from '@/lib/api'
import { reauthenticationMethods } from '@/lib/authMethods'
import { t } from '@/lib/copy'
import { pathFor, useNavigate, useRoute } from '@/lib/router'

type ReauthState = 'loading' | 'ready' | 'redirecting' | 'submitting' | 'completing' | 'cancelled' | 'error'

export function ReauthenticateScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const [state, setState] = useState<ReauthState>(route.query.has('result') ? 'completing' : 'loading')
  const [google, setGoogle] = useState(false)
  const [passwordAvailable, setPasswordAvailable] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)

  const load = useCallback(async (signal?: AbortSignal) => {
    setState('loading')
    setError(null)
    try {
      const [identities, response] = await Promise.all([api.auth.identities(signal), api.auth.methods(signal)])
      const methods = reauthenticationMethods(identities, response)
      setGoogle(methods.google)
      setPasswordAvailable(methods.password)
      setState('ready')
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') return
      setError(t('reauthenticate.error.generic'))
      setState('error')
    }
  }, [])

  useEffect(() => {
    const result = route.query.get('result')
    if (result === 'success') {
      history.replaceState(null, '', pathFor('reauthenticate'))
      setCompleted(true)
      setState('ready')
      return
    }
    history.replaceState(null, '', pathFor('reauthenticate'))
    const controller = new AbortController()
    void load(controller.signal).then(() => {
      if (result === 'cancelled') setState('cancelled')
      else if (result) {
        setError(result === 'expired' ? t('signIn.error.expiredAttempt') : t('reauthenticate.error.mismatch'))
        setState('error')
      }
    })
    return () => controller.abort()
  }, [load, navigate, route.query])

  const startGoogle = () => {
    setState('redirecting')
    window.location.assign(api.auth.googleWebStartUrl('reauthenticate', '/settings/reauthenticate'))
  }

  const submitPassword = (event: FormEvent) => {
    event.preventDefault()
    setState('submitting')
    setError(null)
    void api.auth.reauthenticatePassword(username, password).then(() => {
      setCompleted(true)
      setState('ready')
    }).catch((e: unknown) => {
      const failure = e instanceof ApiFailure ? e : null
      setError(failure?.body.code === 'authenticationFailed' ? t('reauthenticate.error.mismatch') : t('reauthenticate.error.generic'))
      setState('error')
    })
  }

  const disabled = state === 'redirecting' || state === 'submitting' || state === 'completing'
  return (
    <Screen id="reauthenticate" state={state} back={{ screen: 'signInMethods' }}>
      <div className="auth-panel stack">
        <p className="body-secondary">{t('reauthenticate.body')}</p>
        {state === 'loading' ? (
          <LoadingRegion><Skeleton height="var(--auth-provider-height)" /></LoadingRegion>
        ) : state === 'completing' ? (
          <Button
            className="provider-button provider-button-google"
            block
            busy
            showBusyLabel
            disabled
          >
            <img className="provider-icon" src="/google-g.svg" alt="" aria-hidden="true" />
            {t('signIn.completing')}
          </Button>
        ) : completed ? (
          <div className="stack-sm">
            <p className="auth-notice" role="status">{t('reauthenticate.success')}</p>
            <Button variant="primary" onClick={() => navigate('signInMethods', {}, { replace: true })}>
              {t('authCollision.cta.methods')}
            </Button>
          </div>
        ) : (
          <>
            {google ? (
              <Button
                className="provider-button provider-button-google"
                block
                busy={state === 'redirecting'}
                showBusyLabel
                disabled={disabled}
                onClick={startGoogle}
              >
                <img className="provider-icon" src="/google-g.svg" alt="" aria-hidden="true" />
                {state === 'redirecting' ? t('signIn.redirecting.google') : t('signIn.provider.google')}
              </Button>
            ) : null}
            {passwordAvailable ? (
              <form className="stack" onSubmit={submitPassword}>
                <TextField label={t('signIn.username.label')} autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />
                <TextField label={t('signIn.password.label')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button variant="primary" block type="submit" busy={state === 'submitting'} disabled={disabled || !username || !password}>
                  {state === 'submitting' ? t('signIn.cta.passwordSubmitting') : t('signIn.cta.password')}
                </Button>
              </form>
            ) : null}
            {state === 'cancelled' ? <p className="auth-notice" role="status">{t('signIn.cancelled')}</p> : null}
            {error ? <InlineError>{error}</InlineError> : null}
            {state === 'error' ? (
              <Button variant="secondary" onClick={() => void load()}>{t('cta.retry')}</Button>
            ) : null}
          </>
        )}
      </div>
    </Screen>
  )
}
